import { decorationSchema } from "./decoration.mjs";
import { templateDetailSchema } from "./kaisuuken.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/tierReward.ts
/**
* Mirror of the server's `RewardTierStatus` enum
* (`src/service/helper/event/types.ts`). Kept inline so the contract
* package stays free of server-only imports; the canonical definition
* stays alongside the service code.
*/
const RewardTierStatus = [
	"locked",
	"claimable",
	"claimed"
];
const rewardsSchema = z.object({
	lotteryTokens: z.number().int().positive().optional(),
	decorations: z.array(decorationSchema),
	credits: z.number().int().nonnegative().optional(),
	kaisuukens: z.array(z.object({
		template: templateDetailSchema,
		count: z.number().int().positive()
	})),
	extraPackageBoosts: z.array(z.object({
		templateId: z.string(),
		count: z.number().int().positive(),
		boostPercentage: z.number().int().nonnegative()
	})).optional()
});
const rewardTierStatusSchema = z.object({
	id: z.string(),
	requiredValue: z.number().int(),
	status: z.enum(RewardTierStatus),
	rewards: rewardsSchema
});
const getRewardTiersOutputSchema = z.object({
	canClaim: z.boolean(),
	progress: z.number().int(),
	rewardTiers: z.array(rewardTierStatusSchema)
});
const claimOutputSchema = z.object({ rewards: rewardsSchema });
const claimAllOutputSchema = z.object({ rewards: z.array(rewardsSchema) });
const trackParamsSchema = z.object({
	eventName: z.string(),
	trackKey: z.string()
});
const claimInputSchema = trackParamsSchema.extend({ rewardTierId: z.string() });
const unauthorizedError = { UNAUTHORIZED: {
	status: 401,
	message: "Authentication required"
} };
const notFoundError = { NOT_FOUND: {
	status: 404,
	message: "Event track not found"
} };
const forbiddenError = { FORBIDDEN: {
	status: 403,
	message: "Admin access required"
} };
const alreadyClaimedError = { ALREADY_CLAIMED: {
	status: 409,
	message: "Reward tier already claimed"
} };
const tierLockedError = { TIER_LOCKED: {
	status: 403,
	message: "Reward tier is locked"
} };
const eventPassRequiredError = { EVENT_PASS_REQUIRED: {
	status: 403,
	message: "Required event pass not found"
} };
const invalidTierError = { INVALID_TIER: {
	status: 400,
	message: "Invalid reward tier ID"
} };
const claimNotAllowedError = { CLAIM_NOT_ALLOWED: {
	status: 403,
	message: "Claiming is not allowed for this track"
} };
const tierRewardContract = oc.prefix("/event/{eventName}/tier-rewards/{trackKey}").tag("Tier Reward").router({
	getRewardTiers: oc.route({
		summary: "Get tier reward progress and status",
		method: "GET",
		path: "/"
	}).input(trackParamsSchema).output(getRewardTiersOutputSchema).errors({ ...notFoundError }),
	claim: oc.route({
		summary: "Claim a single reward tier",
		method: "POST",
		path: "/claim"
	}).input(claimInputSchema).output(claimOutputSchema).errors({
		...unauthorizedError,
		...notFoundError,
		...alreadyClaimedError,
		...tierLockedError,
		...invalidTierError,
		...eventPassRequiredError,
		...claimNotAllowedError
	}),
	claimAllAvailable: oc.route({
		summary: "Claim all available reward tiers",
		method: "POST",
		path: "/claim-all-available"
	}).input(trackParamsSchema).output(claimAllOutputSchema).errors({
		...unauthorizedError,
		...notFoundError,
		...claimNotAllowedError
	}),
	reset: oc.route({
		summary: "Reset tier reward progress and claims (admin only)",
		method: "POST",
		path: "/reset",
		outputStructure: "detailed"
	}).input(trackParamsSchema.extend({ progress: z.number().int().nonnegative().optional() })).output(z.object({
		status: z.literal(204),
		body: z.void()
	})).errors({
		...unauthorizedError,
		...forbiddenError,
		...notFoundError
	})
});
//#endregion
export { tierRewardContract };
